"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import type { Session } from "next-auth";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft } from "lucide-react";

import { SignUpDTO } from "@/types";
import { uploadImage } from "@/lib/api/s3";
import { cn, errorIntoString } from "@/lib/utils";
import { UserAccountTypes } from "@/types/user";
import { useSafeReCaptcha } from "@/hooks/useSafeReCaptcha";
import { useOtpReqMutation, useSignUpMutation } from "@/features/auth/authApiInjection";
import { emailSignUpSchema, EmailSignUpSchema } from "@/lib/schemas/email-signup";
import { AffiliateState, clearAffiliation } from "@/features/affiliate/affiliateSlice";
import { detectCountryFromGeolocation } from "@/lib/ip-geolocation";
import { AUTH_CARD_HEADER_TOP_SIGNUP_EMAIL_PX } from "@/lib/auth-card-copy";
import { establishVerifiedWebSession } from "@/lib/auth/establish-verified-session";
import {
	backendUserToSavedLoginAccount,
	preserveSessionForAccountSwitcher,
	upsertSavedLoginAccount,
} from "@/lib/auth/saved-accounts";
import { getSafePostAuthRedirectUrl } from "@/lib/utils/returnUrl";
import { ga4Login, ga4SignUp } from "@/lib/analytics/ga4-ecommerce";

import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import TextInput from "@/components/form/TextInput";
import { useToast } from "@/components/ui/use-toast";
import AvatarInput from "@/components/form/AvatarInput";
import PasswordField from "@/components/form/PasswordField";
import MobileNumberInput from "@/components/form/MobileNumberInput";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import VerificationForm from "@/app/signup/email/verification/components/VerificationForm";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";

import verificationImage from "/public/assets/signup/email/verification/verification.png";
import otpEnterImage from "/public/assets/signup/email/verification/otp-enter.png";

const VERIFICATION_CODE_SEND_ERROR = "We could not send the verification code right now. Please try again shortly.";

type EmailSignUpCardProps = {
	variant?: "page" | "modal";
	returnUrlOverride?: string;
	hideTitle?: boolean;
	description?: string;
	glassmorphic?: boolean;
	hideAvatarUpload?: boolean;
	onBack?: () => void;
	onVerified?: () => void | Promise<void>;
};

export default function EmailSignUpCard({
	variant = "page",
	returnUrlOverride,
	hideTitle = false,
	glassmorphic = false,
	hideAvatarUpload = false,
	onBack,
	onVerified,
}: EmailSignUpCardProps) {
	const { toast } = useToast();
	const router = useRouter();
	const dispatch = useDispatch();
	const { data: session, status, update } = useSession();
	const [signup] = useSignUpMutation();
	const [otpReq, { isLoading: isOtpRequesting }] = useOtpReqMutation();
	const { executeRecaptcha } = useSafeReCaptcha();
	const [isLoading, setIsLoading] = useState(false);
	const [isSendingModalOtp, setIsSendingModalOtp] = useState(false);
	const [modalStage, setModalStage] = useState<"form" | "requestOtp" | "verifyOtp">("form");
	const [modalSession, setModalSession] = useState<Session | null>(null);
	const [modalAuthToken, setModalAuthToken] = useState<string | null>(null);
	const [signupUserId, setSignupUserId] = useState<string | null>(null);
	const [pendingLoginCredentials, setPendingLoginCredentials] = useState<{
		email: string;
		password: string;
	} | null>(null);
	const isModal = variant === "modal";
	const handleBack = onBack ?? (() => router.push("/signup"));
	const activeVerificationSession = modalSession;
	const activeVerificationAuthToken = modalAuthToken ?? modalSession?.idToken ?? null;
	const shouldUseParentSessionForVerification = !modalAuthToken && Boolean(session);

	const form = useForm<EmailSignUpSchema>({
		resolver: zodResolver(emailSignUpSchema),
		mode: "onChange",
		defaultValues: {
			email: "",
			password: { password: "", confirmPassword: "" },
			accountType: UserAccountTypes.business,
			number: "",
		},
	});

	const affiliatedBy = useSelector(
		(state: { affiliate: AffiliateState }) => state.affiliate.affiliatedBy,
	);

	useEffect(() => {
		if (!isModal && status === "authenticated" && session) {
			router.replace("/");
		}
	}, [isModal, router, session, status]);

	if (!isModal && status === "authenticated") {
		return null;
	}

	const requestModalOtp = async (sessionToUse?: Session | null) => {
		const idToken = modalAuthToken ?? sessionToUse?.idToken;
		if (idToken) {
			const backendUrl = (process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com").replace(/\/$/, "");
			const response = await fetch(`${backendUrl}/auth/otp-req`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${idToken}`,
					"Content-Type": "application/json",
					"X-Requested-With": "XMLHttpRequest",
				},
				body: JSON.stringify({}),
			});

			if (!response.ok) {
				throw new Error(VERIFICATION_CODE_SEND_ERROR);
			}
			return;
		}

		await otpReq().unwrap();
	};

	const createVerifiedWebSession = async () => {
		const signupToken = modalAuthToken?.trim();
		if (signupToken) {
			await preserveSessionForAccountSwitcher();
			const { user: backendUser, idToken: freshIdToken } = await establishVerifiedWebSession({
				idToken: signupToken,
				userId: signupUserId,
			});
			upsertSavedLoginAccount(backendUserToSavedLoginAccount(backendUser, freshIdToken));
			setModalAuthToken(null);
			setSignupUserId(null);
			setPendingLoginCredentials(null);
			ga4Login("email");

			if (onVerified) {
				await onVerified();
				return;
			}

			// Session cookie is already set — hard navigation avoids update() hanging (e.g. switch-account flow).
			const destination = getSafePostAuthRedirectUrl(returnUrlOverride ?? "/");
			if (destination.startsWith("http")) {
				window.location.href = destination;
			} else {
				window.location.assign(destination);
			}
			return;
		}

		if (!pendingLoginCredentials) {
			await onVerified?.();
			return;
		}

		let loginCaptchaToken: string | undefined;
		if (executeRecaptcha) {
			loginCaptchaToken = await executeRecaptcha("login");
		}

		if (process.env.NODE_ENV === "production" && !loginCaptchaToken) {
			toast({
				title: "Security verification failed",
				description: "Please refresh the page and log in.",
				variant: "destructive",
			});
			throw new Error("Missing login security token");
		}

		const signInResult = await signIn("credentials", {
			redirect: false,
			email: pendingLoginCredentials.email,
			password: pendingLoginCredentials.password,
			captchaToken: loginCaptchaToken,
			clientType: "web",
		});

		if (signInResult?.error) {
			toast({
				title: "Error signing in",
				description: "Account verified but failed to sign in. Please try logging in.",
				variant: "destructive",
			});
			throw new Error(signInResult.error);
		}

		setPendingLoginCredentials(null);
		ga4Login("email");
		await update();
		if (onVerified) {
			await onVerified();
			return;
		}

		router.replace(returnUrlOverride ?? "/");
		router.refresh();
	};

	const onSubmit = async (values: EmailSignUpSchema) => {
		setIsLoading(true);

		let captchaToken: string | undefined;
		if (executeRecaptcha) {
			try {
				captchaToken = await executeRecaptcha("signup");
			} catch (error) {
				console.error("reCAPTCHA error:", error);
				if (process.env.NODE_ENV === "production") {
					toast({
						title: "Security verification failed",
						description: "Please try again.",
						variant: "destructive",
					});
					setIsLoading(false);
					return;
				}
			}
		} else if (process.env.NODE_ENV === "production") {
			toast({
				title: "Security verification failed",
				description: "Please refresh the page and try again.",
				variant: "destructive",
			});
			setIsLoading(false);
			return;
		}

		if (process.env.NODE_ENV === "production" && !captchaToken) {
			toast({
				title: "Security verification failed",
				description: "Please refresh the page and try again.",
				variant: "destructive",
			});
			setIsLoading(false);
			return;
		}

		const detectedLocation = await detectCountryFromGeolocation();
		let avatar = values.avatar;
		if (values.avatar) {
			avatar = await uploadImage(values.avatar);
		}

		const body: SignUpDTO = {
			clientType: "web",
			email: String(values.email),
			password: String(values.password.password),
			firstName: String(values.firstName),
			lastName: String(values.lastName),
			accountType: typeof values.accountType === "string" ? values.accountType : (values.accountType ?? UserAccountTypes.business),
			...(values.company && { companyName: String(values.company) }),
			...(values.designation && { designation: String(values.designation) }),
			...(values.department && typeof values.department === "string" ? { department: values.department } : {}),
			...(values.accountUse && typeof values.accountUse === "string" ? { accountUse: values.accountUse } : {}),
			...(values.numberOfEmployees && typeof values.numberOfEmployees === "string" ? { numberOfEmployees: values.numberOfEmployees } : {}),
			...(values.organizationType && typeof values.organizationType === "string" ? { organizationType: values.organizationType } : {}),
			...(values.number && values.number.trim() !== "" && { mobileNumber: String(values.number) }),
			...(values.website && { website: String(values.website) }),
			...(avatar && { avatar: String(avatar) }),
			...(affiliatedBy && { referralCode: String(affiliatedBy) }),
			...(values.city && { city: String(values.city) }),
			...(values.country ? { country: String(values.country) } : detectedLocation.country ? { country: detectedLocation.country } : {}),
			...(detectedLocation.countryCode && { countryCode: detectedLocation.countryCode }),
			...(values.state && { state: String(values.state) }),
			...(captchaToken && { captchaToken: String(captchaToken) }),
		};

		if (process.env.NODE_ENV === "development") {
			console.log("Signup request body:", JSON.stringify(body, null, 2));
		}

		try {
			const signupResponse = await signup(body).unwrap();
			dispatch(clearAffiliation());
			ga4SignUp("email");

			if (!signupResponse?.id_token) {
				toast({
					title: "Verification setup failed",
					description: "Please log in and request a verification code.",
					variant: "destructive",
				});
				setIsLoading(false);
				return;
			}

			setModalAuthToken(signupResponse.id_token);
			const createdUser = signupResponse.user as { id?: string } | undefined;
			setSignupUserId(createdUser?.id ? String(createdUser.id) : null);
			setPendingLoginCredentials({
				email: String(values.email),
				password: String(values.password.password),
			});
			setModalSession(null);
			setModalStage("requestOtp");
			setIsLoading(false);
			return;
		} catch (error) {
			const ex = errorIntoString(error);
			toast({
				title: "Error signing up",
				description: ex,
				variant: "destructive",
			});
			setIsLoading(false);
		}
	};

	const handleModalOtpRequest = async () => {
		setIsSendingModalOtp(true);
		try {
			await requestModalOtp(modalSession);
			toast({ title: "Verification Code Sent" });
			setModalStage("verifyOtp");
		} catch {
			toast({
				variant: "destructive",
				title: "Code Verification Error",
				description: VERIFICATION_CODE_SEND_ERROR,
			});
		} finally {
			setIsSendingModalOtp(false);
		}
	};

	if (modalStage !== "form") {
		return (
			<Card className="max-w-md rounded-3xl border-none px-1 !shadow-custom sm:px-6">
				<CardHeader className="flex flex-col items-center gap-[14px] space-y-0 px-6 pb-0 pt-10 sm:px-8">
					<CardTitle className="text-center text-xl font-semibold leading-snug sm:text-2xl">
						Account <span className="text-accent-foreground">Verification</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="px-6 pb-8 pt-6 text-center sm:px-8">
					{!activeVerificationAuthToken ? (
						<div className="flex flex-col items-center gap-3 py-8">
							<LoadingSpinner fullScreen={false} layout="inline" />
							<p className="text-sm text-muted-foreground">Preparing verification...</p>
						</div>
					) : isSendingModalOtp ? (
						<div className="flex flex-col items-center gap-3 py-8">
							<LoadingSpinner fullScreen={false} layout="inline" />
							<p className="text-sm text-muted-foreground">Sending verification code...</p>
						</div>
					) : modalStage === "requestOtp" ? (
						<div className="flex flex-col items-center gap-5">
							<Image
								draggable={false}
								src={verificationImage}
								alt=""
								aria-hidden
								quality={100}
								sizes="160px"
								className="w-36 sm:w-40"
							/>
							<p className="text-sm text-muted-foreground">
								The verification code will be sent to your email.
							</p>
							<Button onClick={handleModalOtpRequest} size="lg" disabled={isOtpRequesting || isSendingModalOtp}>
								{isOtpRequesting || isSendingModalOtp ? <LoadingSpinner fullScreen={false} layout="inline" /> : "Request code"}
							</Button>
						</div>
					) : (
						<div className="flex flex-col items-center gap-5">
							<Image
								draggable={false}
								src={otpEnterImage}
								alt=""
								aria-hidden
								quality={100}
								sizes="180px"
								className="w-40 sm:w-44"
							/>
							<p className="text-sm text-muted-foreground">
								We have sent a verification code to your email.
							</p>
							<VerificationForm
								session={shouldUseParentSessionForVerification ? session : null}
								authToken={activeVerificationAuthToken ?? undefined}
								updateSession={shouldUseParentSessionForVerification ? update : undefined}
								returnUrlOverride={returnUrlOverride}
								onVerified={createVerifiedWebSession}
							/>
							<div className="inline-flex items-center text-sm text-grey">
								<span>Didn&apos;t receive a code?</span>
								<Button
									variant="ghost"
									className="ml-1 h-fit px-0 text-accent-foreground underline hover:bg-transparent"
									onClick={handleModalOtpRequest}
									disabled={isOtpRequesting || isSendingModalOtp}
								>
									Resend
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		);
	}

	return (
		<Card
			className={cn(
				"relative max-w-md rounded-3xl px-1 sm:px-6",
				glassmorphic
					? "glassmorphism-card border border-[color:var(--glass-border)] bg-[var(--glass-background)] shadow-[var(--glass-shadow)] backdrop-blur-xl [-webkit-backdrop-filter:var(--glass-backdrop-filter)] [backdrop-filter:var(--glass-backdrop-filter)]"
					: "border-none !shadow-custom",
			)}
			aria-busy={isLoading}
		>
			{isModal && hideTitle && onBack ? (
				<button
					type="button"
					onClick={onBack}
					className="absolute left-4 top-4 z-10 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-secondary transition hover:bg-secondary/10 hover:text-secondary sm:left-5 sm:top-5"
					aria-label="Back to signup options"
				>
					<ChevronLeft className="size-5" />
				</button>
			) : null}
			{isLoading ? (
				<div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-3xl bg-background/75 backdrop-blur-sm">
					<LoadingSpinner fullScreen={false} layout="inline" />
					<p className="text-sm text-muted-foreground">Creating your account...</p>
				</div>
			) : null}
			<CardHeader
				className="flex flex-col items-center gap-[14px] space-y-0 px-6 pb-0 sm:px-8"
				style={{
					paddingTop: hideTitle ? 32 : AUTH_CARD_HEADER_TOP_SIGNUP_EMAIL_PX + (variant === "modal" ? 12 : 0),
				}}
			>
				{hideTitle ? null : (
					<div
						className={cn(
							"flex w-full items-center justify-center",
							isModal ? "relative" : "gap-1 sm:gap-2",
						)}
					>
						{isModal && onBack ? (
							<button
								type="button"
								onClick={onBack}
								className="absolute left-0 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-secondary transition hover:bg-secondary/10 hover:text-secondary sm:-left-4"
								aria-label="Back to signup options"
							>
								<ChevronLeft className="size-5" />
							</button>
						) : null}
						{!isModal ? (
							<button
								type="button"
								onClick={handleBack}
								className="-ml-8 mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-secondary transition hover:bg-secondary/10 hover:text-secondary sm:-ml-10 sm:mr-2"
								aria-label="Back to signup options"
							>
								<ChevronLeft className="size-5" />
							</button>
						) : null}
						<CardTitle
							className={cn(
								"font-semibold leading-snug",
								isModal ? "pl-10 text-lg sm:pl-8 sm:text-xl md:text-2xl" : "text-lg xs:text-xl sm:text-xl md:text-2xl",
							)}
						>
							Create a <span className="text-accent-foreground">new account</span>
						</CardTitle>
					</div>
				)}
			</CardHeader>
			<CardContent className="px-6 pt-[20px] sm:px-8">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex min-h-0 flex-col justify-center [&_div[class*='py-2']]:!min-h-0 [&_div[class*='py-2']]:!py-0"
					>
						{hideAvatarUpload ? null : (
							<div className="mx-auto mb-[10px] size-28">
								<AvatarInput name="avatar" control={form.control} border={false} />
							</div>
						)}

						<div className="space-y-[10px] py-[10px]">
							<div className="flex flex-col space-y-[10px]">
								<TextInput
									name="firstName"
									label="Name"
									placeholder="First name"
									autoComplete="given-name"
								/>
								<TextInput name="lastName" label="" placeholder="Last name" autoComplete="family-name" />
							</div>
							<TextInput name="email" label="Email" placeholder="Email" autoComplete="email" />
							<MobileNumberInput name="number" label="Mobile" />
							<div className="flex flex-col space-y-[10px]">
								<PasswordField name="password" label="Password" />
							</div>
						</div>

						<Button
							className="custom-box-shadow mx-auto mt-8 h-12 w-[calc(100%-32px)] text-xs"
							variant="accent"
							type="submit"
							disabled={isLoading}
						>
							{isLoading ? <LoadingSpinner fullScreen={false} layout="inline" /> : "Create account"}
						</Button>
					</form>
				</Form>
			</CardContent>
			<CardFooter className="flex flex-col items-center gap-2 pb-[40px]">
				<h1 className="text-center text-[0.625rem] font-normal">
					By joining, you agree to our{" "}
					<Link className="text-secondary" href={"/compliance"}>
						Terms of Service{" "}
					</Link>
					. Please review our{" "}
					<Link className="text-secondary" href={"/compliance"}>
						Privacy Policy{" "}
					</Link>
					to understand how we handle your personal information.
				</h1>
			</CardFooter>
		</Card>
	);
}
